// The History section of every invoice modal (owner, 2026-10-08 —
// `docs/decisions.md` D-329): Decision History and Comments, combined, plus a
// row each time someone changed the invoice's Total. `lib/history.ts` writes
// every line and picks every colour; this draws them, loads the comment
// thread, and keeps the comment box the Comments section had.
//
// A TIMELINE (owner, 2026-10-08 — D-330, from the approved mockup): a 10px dot
// per row on a 2px light gray line; the act in bold with " by <name>" and the
// time at the right; the step in a small gray caption; the detail in light
// gray boxes. The first row is the upload, with the AI's notes under it, which
// replaced the AI Notes section. `HistoryTimeline` draws any list of rows.
//
// Beside the heading, whether the section is open or not: a yellow pill when
// the Total has been changed ("Alex Morgan changed the Total"), a blue one when
// anyone left a written comment ("Alex Morgan left a comment") — posted, or on
// a decision or a hold (D-330) — and the existing "Mentioned you" pill.
//
// THE COMMENT BOX IS THE LAST NODE: an open blue dot, then one bordered field
// holding the box and Post. Its behaviour is carried over unchanged from
// `CommentThread.tsx`: @mentions with a typeahead over the directory, and a
// post that sends only the body and the mention list. The API stamps the
// author, the step and the comment's visibility, so the client never names an
// owner. The mention email is the API's job. No box under the Analytics lens:
// the modal passes no `user` there, because a comment is a write.
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Badge, Body1, Button, Caption1, Spinner, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import type {
  Conversation,
  Correction,
  CurrentUser,
  Decision,
  DirectoryUser,
  Hold,
  Invoice,
  InvoiceNotificationSummary,
  InvoiceUploadSummary,
} from '../lib/types'
import { loadConversations, createConversation, searchPeople } from '../lib/data'
import { errorMessage } from '../api/errors'
import {
  buildHistory,
  commentsPillLabel,
  HISTORY_TONE_COLOR,
  totalChangedPillLabel,
  writtenComments,
  type HistoryEntry,
  type HistoryTone,
} from '../lib/history'
import { TINT_PILLS } from '../lib/theme'
import { CollapsibleSection } from './CollapsibleSection'
import { DotPill } from './Badges'

/** The dot's colour per tone; gray is a neutral token, the rest the pills' dots. */
const DOT_COLOR: Readonly<Record<HistoryTone, string>> = {
  ...HISTORY_TONE_COLOR,
  gray: tokens.colorNeutralForeground4,
}

const useStyles = makeStyles({
  none: { color: tokens.colorNeutralForeground3, fontStyle: 'italic' },
  // THE TIMELINE (D-330). Each row is a rail (the dot and the line under it)
  // and its content; the line runs on to the next row's dot.
  timeline: { display: 'flex', flexDirection: 'column', margin: 0, paddingLeft: '8px', listStyleType: 'none' },
  row: { display: 'flex', gap: '12px' },
  // A HISTORY ROW FILLS ON HOVER (owner, 2026-10-12 — D-350 amended), the
  // hover the GL Summary and Line Items Summary rows have: those are Fluent
  // `TableRow`s, whose hover is these two tokens, so the timeline takes the
  // same two rather than a look-alike. Not the comment box's row.
  entryRow: {
    ':hover': { backgroundColor: tokens.colorSubtleBackgroundHover, color: tokens.colorNeutralForeground1Hover },
  },
  rail: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '12px',
    flexShrink: 0,
    paddingTop: '5px',
  },
  // The comment box's dot sits level with the middle of its field.
  railComposer: { paddingTop: '11px' },
  dot: { width: '10px', height: '10px', borderRadius: '50%', flexShrink: 0, boxSizing: 'border-box' },
  // The comment box's node: an open (outlined) blue dot.
  dotOpen: {
    border: `2px solid ${HISTORY_TONE_COLOR.blue}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  line: { width: '2px', flexGrow: 1, backgroundColor: tokens.colorNeutralStroke2 },
  content: { flexGrow: 1, minWidth: 0, paddingBottom: '12px' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '12px' },
  action: { fontWeight: tokens.fontWeightSemibold },
  when: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    whiteSpace: 'nowrap',
  },
  caption: {
    display: 'block',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
  },
  // "Click here for details." at the end of its box (D-343): a link, inline.
  moreLink: {
    minWidth: 0,
    height: 'auto',
    padding: 0,
    verticalAlign: 'baseline',
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorBrandForegroundLink,
    // Underlined, so it reads as a link inside a sentence (owner, 2026-10-09).
    textDecorationLine: 'underline',
    ':hover': { color: tokens.colorBrandForegroundLinkHover, textDecorationLine: 'underline' },
  },
  // What the link opens, set apart as belonging to it: indented behind a rule
  // line, each box lighter than the one above and outlined.
  moreItems: {
    marginTop: '6px',
    marginLeft: '16px',
    paddingLeft: '10px',
    borderLeft: `2px solid ${tokens.colorNeutralStroke2}`,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  moreItem: {
    padding: '6px 10px',
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke3}`,
    color: tokens.colorNeutralForeground2,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  detail: {
    marginTop: '6px',
    padding: '6px 10px',
    borderRadius: tokens.borderRadiusLarge,
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  // THE COMMENT BOX: the textarea and Post in ONE bordered field, on one row.
  // The field is relative so the mention menu anchors just below it.
  composer: { flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '4px' },
  field: {
    position: 'relative',
    display: 'flex',
    alignItems: 'flex-end',
    gap: '8px',
    padding: '4px 4px 4px 10px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderBottomColor: tokens.colorNeutralStrokeAccessible,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    ':focus-within': { borderBottomColor: tokens.colorCompoundBrandStroke },
  },
  textarea: {
    flexGrow: 1,
    minWidth: 0,
    minHeight: '28px',
    padding: '4px 0',
    border: 'none',
    outlineStyle: 'none',
    resize: 'vertical',
    backgroundColor: 'transparent',
    color: tokens.colorNeutralForeground1,
    fontFamily: 'inherit',
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    '::placeholder': { color: tokens.colorNeutralForeground4 },
  },
  mentionHint: { color: tokens.colorNeutralForeground3, fontSize: '11px' },
  error: { color: tokens.colorPaletteRedForeground1 },
  // Floating @-mention typeahead. Not portaled (renders inside this component's
  // DOM) so it stays inside the modal's focus trap.
  menu: {
    position: 'absolute',
    top: '100%',
    left: '0',
    marginTop: '4px',
    zIndex: 100,
    minWidth: '260px',
    maxWidth: '100%',
    maxHeight: '220px',
    overflowY: 'auto',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    padding: '4px',
  },
  menuItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
    padding: '6px 10px',
    borderRadius: tokens.borderRadiusSmall,
    cursor: 'pointer',
  },
  menuItemActive: { backgroundColor: tokens.colorNeutralBackground1Selected },
  menuItemName: { fontSize: '13px', fontWeight: tokens.fontWeightMedium },
  menuItemMail: { fontSize: '11px', color: tokens.colorNeutralForeground3 },
  menuHint: { padding: '6px 10px', color: tokens.colorNeutralForeground3, fontSize: '12px' },
})

interface Props {
  /** The invoice the history and the thread hang off. */
  parentId: string
  /** The header as the detail read returned it: the upload row's page span,
      its fallback address and time, and the AI's notes under it (D-330). */
  invoice: Invoice
  /** `include=upload` (D-330); null when it could not be read, undefined
      until the read lands. */
  upload: InvoiceUploadSummary | null | undefined
  /** `include=notifications` (D-335): the notifications sent about the upload, a gray
      "Notification sent to …" row each. Never counted as comments. */
  notifications?: readonly InvoiceNotificationSummary[]
  /** From the modal's detail read; 'loading' until it lands. */
  decisions: Decision[] | 'loading'
  holds: Hold[]
  totalChanges: Correction[]
  /** Signed-in user. Without it (the Analytics lens) there is no comment box
      and no "Mentioned you" pill. */
  user?: CurrentUser
  /** Bumped when some OTHER control just wrote a comment on this thread (the
      editor's Manually Entered reason) — re-reads it without remounting, so a
      half-typed draft survives. */
  refreshNonce?: number
  /** The first section in its modal draws no rule above itself. */
  divider?: boolean
}

interface PickedMention {
  /** The directory address written to `conversations.mentions`. */
  id: string
  /** Display name as inserted into the body (@name). */
  name: string
}

interface MentionState {
  query: string
  /** Index of the '@' in the body. */
  start: number
  /** Caret position when the token was last read. */
  caret: number
}

// True if `@name` still appears in the body as a whole mention — the char after
// the name must be absent or a non-word char, so "@John" isn't kept live by
// "@Johnathan". Names contain spaces, so a word-boundary on the full name holds.
function mentionIsLive(body: string, name: string): boolean {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
  return new RegExp(`@${escaped}(?![\\p{L}\\p{N}])`, 'u').test(body)
}

// The in-progress @-token immediately left of the caret, or null. A mention
// token is '@' at the start or after whitespace, followed by non-whitespace.
function findMention(value: string, caret: number): MentionState | null {
  const upto = value.slice(0, caret)
  const m = /(?:^|\s)@(\S*)$/.exec(upto)
  if (!m) return null
  const query = m[1]
  return { query, start: caret - query.length - 1, caret }
}

/** A dot, and the line on to the next row unless this is the last. */
function Rail({ tone, open = false, last, composer = false }: { tone: HistoryTone; open?: boolean; last: boolean; composer?: boolean }) {
  const s = useStyles()
  return (
    <div className={mergeClasses(s.rail, composer && s.railComposer)} aria-hidden="true">
      <span className={mergeClasses(s.dot, open && s.dotOpen)} style={open ? undefined : { backgroundColor: DOT_COLOR[tone] }} />
      {!last && <span className={s.line} />}
    </div>
  )
}

/** One timeline row: the act and the time, the caption, the boxes. */
function HistoryRow({ entry, last }: { entry: HistoryEntry; last: boolean }) {
  const s = useStyles()
  return (
    <li className={mergeClasses(s.row, s.entryRow)} data-tone={entry.tone} data-kind={entry.kind}>
      <Rail tone={entry.tone} last={last} />
      <div className={s.content}>
        <div className={s.head}>
          <Body1>
            <span className={s.action}>{entry.action}</span>
            {entry.actor && ` ${entry.actorJoin} ${entry.actor}`}
          </Body1>
          {entry.when && <span className={s.when}>{entry.when}</span>}
        </div>
        {entry.caption && <span className={s.caption}>{entry.caption}</span>}
        {entry.details.map((d, i) => (
          <Body1 block key={i} className={s.detail}>
            {d}
          </Body1>
        ))}
        {entry.more && <MoreBox lead={entry.more.lead} items={entry.more.items} />}
      </div>
    </li>
  )
}

/** A box that folds what is under it (D-343): its sentence, then "Click here
    for details.", which opens the items beneath it. Starts closed. */
function MoreBox({ lead, items }: { lead: string; items: readonly string[] }) {
  const s = useStyles()
  const [open, setOpen] = useState(false)
  return (
    <>
      <Body1 block className={s.detail}>
        {lead}{' '}
        <Button appearance="transparent" className={s.moreLink} aria-expanded={open} onClick={() => setOpen((v) => !v)}>
          {/* It flips once open (owner, 2026-10-09). */}
          {open ? 'Hide details.' : 'Click here for details.'}
        </Button>
      </Body1>
      {open && (
        <div className={s.moreItems}>
          {items.map((item, i) => (
            <Body1 block key={i} className={s.moreItem}>
              {item}
            </Body1>
          ))}
        </div>
      )}
    </>
  )
}

/** The timeline: every row, then (optionally) a last node of its own — the
    comment box. Without one, the last row draws no line below its dot. */
export function HistoryTimeline({ entries, footer }: { entries: readonly HistoryEntry[]; footer?: ReactNode }) {
  const s = useStyles()
  return (
    <ol className={s.timeline}>
      {entries.map((e, i) => (
        <HistoryRow key={e.key} entry={e} last={!footer && i === entries.length - 1} />
      ))}
      {footer && (
        <li className={s.row}>
          <Rail tone="blue" open last composer />
          {footer}
        </li>
      )}
    </ol>
  )
}

export function InvoiceHistory({
  parentId,
  invoice,
  upload,
  notifications = [],
  decisions,
  holds,
  totalChanges,
  user,
  refreshNonce,
  divider = true,
}: Props) {
  const s = useStyles()
  const [comments, setComments] = useState<Conversation[] | 'loading'>('loading')
  const [body, setBody] = useState('')
  const [mentions, setMentions] = useState<PickedMention[]>([])
  const [posting, setPosting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Active @-mention token + typeahead results.
  const [mention, setMention] = useState<MentionState | null>(null)
  const [results, setResults] = useState<DirectoryUser[]>([])
  const [resultsLoading, setResultsLoading] = useState(false)
  const [highlight, setHighlight] = useState(0)

  const taRef = useRef<HTMLTextAreaElement | null>(null)
  const loadSeq = useRef(0)
  const searchSeq = useRef(0)

  const reload = useCallback(async () => {
    const seq = ++loadSeq.current
    if (!parentId) {
      setComments([])
      return
    }
    setComments('loading')
    try {
      const rows = await loadConversations(parentId)
      if (seq === loadSeq.current) setComments(rows)
    } catch (e) {
      if (seq !== loadSeq.current) return
      setComments([])
      setError(`Could not load comments: ${errorMessage(e)}`)
    }
  }, [parentId])

  useEffect(() => {
    reload()
    // `refreshNonce` forces a re-read when a sibling control has just written
    // to this same thread out of band.
  }, [reload, refreshNonce])

  // Debounced directory search for the active @-token (seq-guarded so a slow
  // earlier response can't overwrite a newer one).
  useEffect(() => {
    if (!mention) {
      setResults([])
      setResultsLoading(false)
      return
    }
    const my = ++searchSeq.current
    setResultsLoading(true)
    const t = setTimeout(() => {
      searchPeople(mention.query, 8)
        .then((res) => {
          if (my !== searchSeq.current) return
          setResults(res)
          setHighlight(0)
        })
        .catch(() => {
          if (my === searchSeq.current) setResults([])
        })
        .finally(() => {
          if (my === searchSeq.current) setResultsLoading(false)
        })
    }, 200)
    return () => clearTimeout(t)
  }, [mention])

  function onChange(value: string, el: HTMLTextAreaElement) {
    setBody(value)
    const caret = el.selectionStart ?? value.length
    setMention(findMention(value, caret))
  }

  function selectMention(u: DirectoryUser) {
    if (!mention) return
    const name = u.display_name || u.mail || u.upn || 'user'
    const insert = `@${name} `
    const next = body.slice(0, mention.start) + insert + body.slice(mention.caret)
    setBody(next)
    // Store the directory address, because that is what the profile's
    // directory_email is compared against by the mentioned-you indicator.
    const id = u.mail || u.upn
    if (id) {
      setMentions((prev) =>
        prev.some((m) => m.id.toLowerCase() === id.toLowerCase()) ? prev : [...prev, { id, name }],
      )
    }
    setMention(null)
    setResults([])
    // Restore the caret to just after the inserted mention on the next frame,
    // once the new value has rendered.
    const newCaret = mention.start + insert.length
    requestAnimationFrame(() => {
      const ta = taRef.current
      if (ta) {
        ta.focus()
        ta.setSelectionRange(newCaret, newCaret)
      }
    })
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (!mention || results.length === 0) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlight((h) => (h + 1) % results.length)
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlight((h) => (h - 1 + results.length) % results.length)
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault()
      selectMention(results[highlight] ?? results[0])
    } else if (e.key === 'Escape') {
      e.preventDefault()
      setMention(null)
    }
  }

  async function handlePost() {
    const text = body.trim()
    if (!text || !user || posting) return
    setPosting(true)
    setError(null)
    try {
      // Keep only mentions whose @name is still present in the body (the user may
      // have deleted the text after picking). Dedupe by identifier.
      const ids = [...new Set(mentions.filter((m) => mentionIsLive(body, m.name)).map((m) => m.id))]
      await createConversation(parentId, text, ids)
      setBody('')
      setMentions([])
      setMention(null)
      await reload()
    } catch (e) {
      setError(`Could not post the comment: ${errorMessage(e)}`)
    } finally {
      setPosting(false)
    }
  }

  // Per-record "mentioned you": any comment whose mention list carries the
  // signed-in user's directory address (case-insensitive). That address — not the
  // user principal name — is what a mention stores, and the two can differ.
  const myIds = [user?.directoryEmail, user?.email].filter((v): v is string => !!v).map((v) => v.toLowerCase())
  const mentionedMe =
    comments !== 'loading' &&
    myIds.length > 0 &&
    comments.some((c) =>
      (c.mentions ?? [])
        .map((x) => x.trim().toLowerCase())
        .filter(Boolean)
        .some((m) => myIds.includes(m)),
    )

  const liveMentions = mentions.filter((m) => mentionIsLive(body, m.name))
  const loading = decisions === 'loading' || comments === 'loading'
  const entries = loading ? [] : buildHistory({ decisions, holds, comments, totalChanges, invoice, upload, notifications })
  const totalPill = totalChangedPillLabel(totalChanges)
  // Every written comment, posted or on a decision or a hold (D-330).
  const commentPill = loading ? null : commentsPillLabel(writtenComments({ decisions, holds, comments }))

  const composer = user && (
    <div className={s.composer}>
      <div className={s.field}>
        <textarea
          ref={taRef}
          className={s.textarea}
          aria-label="Add a comment"
          placeholder="e.g. @colleague, can you confirm this Total with the vendor?"
          value={body}
          rows={1}
          disabled={posting}
          onChange={(e) => onChange(e.target.value, e.target)}
          onKeyDown={onKeyDown}
          onBlur={() => setMention(null)}
        />
        <Button appearance="primary" size="small" disabled={!body.trim() || posting} onClick={handlePost}>
          {posting ? 'Posting…' : 'Post'}
        </Button>

        {mention && (
          <div className={s.menu}>
            {resultsLoading && (
              <div className={s.menuHint}>
                <Spinner size="tiny" label="Searching people…" />
              </div>
            )}
            {!resultsLoading && results.length === 0 && <div className={s.menuHint}>No people found.</div>}
            {results.map((u, i) => (
              <div
                key={u.id}
                className={mergeClasses(s.menuItem, i === highlight && s.menuItemActive)}
                // Pick on mousedown so the textarea keeps focus (preventDefault
                // stops the blur that would otherwise close the menu first).
                onMouseDown={(e) => {
                  e.preventDefault()
                  selectMention(u)
                }}
                onMouseEnter={() => setHighlight(i)}
              >
                <span className={s.menuItemName}>{u.display_name || u.mail || u.upn || 'Unknown'}</span>
                {(u.mail || u.upn) && <span className={s.menuItemMail}>{u.mail || u.upn}</span>}
              </div>
            ))}
          </div>
        )}
      </div>
      {liveMentions.length > 0 && (
        <span className={s.mentionHint}>Mentioning: {liveMentions.map((m) => m.name).join(', ')}</span>
      )}
    </div>
  )

  return (
    <CollapsibleSection
      title="History"
      divider={divider}
      adornments={
        <>
          {totalPill && <DotPill pill={TINT_PILLS.yellow}>{totalPill}</DotPill>}
          {commentPill && <DotPill pill={TINT_PILLS.blue}>{commentPill}</DotPill>}
          {mentionedMe && (
            <Badge appearance="tint" color="brand" size="small">
              Mentioned you
            </Badge>
          )}
          {loading && <Spinner size="tiny" />}
        </>
      }
    >
      {error && <Caption1 className={s.error}>{error}</Caption1>}

      {loading ? (
        <Body1 block className={s.none}>Loading history…</Body1>
      ) : entries.length === 0 && !composer ? (
        <Body1 block className={s.none}>Nothing recorded yet.</Body1>
      ) : (
        <HistoryTimeline entries={entries} footer={composer || undefined} />
      )}
    </CollapsibleSection>
  )
}
